/**
 * Programmer's note for this file. The condition if ( variable == null )
 * will check both the undefined and null case. Note the '==' and not '==='
 * This is often better than using if ( !variable ) because empty strings and
 * 0 number values are evaluated as 'falsey'
 */
import * as Highstock from 'highcharts/highstock';
import * as moment from 'moment';
import 'moment-timezone';

import { IDataset } from './dataset';
import { AxisFormat } from './menu-options';

/** The legend contains labels for each dataset as well as shows values hovered by the user. */
export function legendFormatter( datasets: IDataset[], showSingleLegendXValue: boolean ) {
    return function() {
        const series = this;
        let styledSeriesName = '<span style="color:' + this.color + '">' + this.name + '</span>';

        // show this for hidden series
        if ( !this.visible ) {
            return styledSeriesName + ': disabled';
        }

        // if there's no data just return a hyphen (returning nothing can mess with the legend height calculation)
        // this also catches the case where no points are hovered
        if ( series == null || series.chart.hoverPoints == null ) {
            return styledSeriesName + '-';
        }
        const point = series.point;
        const isFullResolution = datasets.every( dataset => dataset.isFullResolution );
        let s = styledSeriesName + ': ';

        if ( series.name === 'range' ) {
            // auto-hide the range series in the legend when it's only showing info on a single full-res data point
            if ( point == null ) {
                return '-';
            } else {
                // Make sure the decimals are rounded to the correct precision
                s += point.low + ' - ' + point.high;
            }
        } else {
            if ( !showSingleLegendXValue ) {
                // show an x-value next to each series name in the legend, instead of having one x-value for the entire legend.
                // but don't show a separate x-value for each range series.
                const offset = datasets.find( dataset => series.options.id === dataset.uid ).offset as number;
                styledSeriesName = generateXLegendString( this, point, offset ) + ' ' + styledSeriesName;
                s = styledSeriesName + ': ';
            }

            if ( datasets[0] ) {
                const conversions = datasets[0].metadata.typeConversions;
                for ( let i = 0; i < conversions.length; i++ ) {
                    if ( conversions[i].value === point.y ) {
                        point.stateLabel = conversions[i].state;
                        break;
                    }
                }
            }

            if ( point == null ) {
                s += '<span class="no-data">NO DATA</span>';
            } else if ( point.y != null ) {
                if ( point.stateLabel ) {
                    s += ' State: ' + point.stateLabel + ' (DN: ' + point.y + ')';
                } else {
                    if ( point.y.toString().indexOf('e') !== -1 ) { // If scientific notation
                        // Make sure the decimal is rounded to the correct precision
                        s += ' ' + point.y.toExponential( 2 );
                    } else {
                        // Make sure the decimal is rounded to the correct precision
                        s += ' ' + point.y;
                    }
                    // if we're not at full resolution, or this value represents a data group,
                    // add an indication that this is an average value
                    if ( !isFullResolution || point.dataGroup != null ) {
                        s += ' (avg)';
                    }
                }
            } else {
                throw new Error('Error: point type unrecognized for legend label formatter: ' + point);
            }
        }

        return s;
    };
}

/**
 * Generates a string to be used in the chart's legend, which represents the hovered point's x-value. The point may represent
 * a single data point, or a group/bin of points.
 * @param point Point object from highcharts
 * @param xOffset If this is non-zero, the point has been offset from its origonal postion; we need to account for this in our calculation
 */
export function generateXLegendString( legendOptions, point: any, xOffset = 0 ) {
    const group = point.dataGroup;
    // If data grouping is off, or the data group represents only one point, show a single x-value.
    // Once latis is able to serve start and end times for binave data, we should display those here
    // (instead of a single date) when not viewing full-res data.
    if ( group == null || group.length === 1 ) {
        return legendXValueFormatter( legendOptions, point.x + xOffset );
    } else {
        // otherwise, show an x-range from the start of the group to the end of the group.
        // Once latis is able to serve start and end times for bins, this should show the start time
        // of the first bin and the end time of the last bin in the data group.
        const series = point.series;
        const data = series.options.data;
        const xStart = data[ group.start][0];
        const xEnd = data[ group.start + group.length - 1][0];
        return legendXValueFormatter( legendOptions, xStart + xOffset, xEnd + xOffset );
    }
}

/**
 * Formats the x-value in the chart's legend. Takes two arguments when data grouping is in effect, or
 * the data is not at full resolution, and one "point" represents a range of x-values. Takes one argument ortherwise.
 */
export function legendXValueFormatter( legendOptions, xVal1, xVal2? ) {
    // if xVal1 is a non-number, show a hyphen
    xVal1 = ( typeof xVal1 !== 'number' || isNaN(xVal1) ) ? '-' : xVal1;

    if ( typeof legendOptions.legendFormatter === 'function' ) {
        return legendOptions.legendFormatter( xVal1, xVal2 );
    }

    let legendText;
    if ( legendOptions.labelFormat === 'raw' ) {
        // Output the x-axis title, then the value(s)
        legendText = legendOptions.options.xAxis[0].title.text + ': ' + xVal1;
        if ( xVal2 != null ) {
            legendText += ' - ' + xVal2;
        }
    } else {
        // if the format is 'auto', null, or undefined
        legendText = ( xVal1 === '-' ) ? '-' : formatXValueAsTime( xVal1 );
        if ( xVal2 != null ) {
            legendText += ' - ' + formatXValueAsTime( xVal2 );
        }
    }
    return legendText;

    function formatXValueAsTime( xVal ) {
        // Output formatted time
        legendOptions.momentTimeFormat = 'YYYY-MM-DD';
        const tempDate = moment.utc( Highstock.dateFormat('%Y-%m-%dT%H:%M:%S', xVal) );
        const tzAbbr = moment.tz( legendOptions.timezone ).zoneAbbr();
        return tempDate.format( legendOptions.momentTimeFormat + 'THH:mm:ss') + ' ' + tzAbbr;
    }
}

/** Updates the legend displayed series values with what is currently hovered by the users mouse */
export function updateTooltip( chart, enabled: boolean, showSingleLegendXValue: boolean ) {
    if ( !enabled || !chart ) { return; }

    // now we have to update our values in the legend which is serving as our tooltip
    const legendOptions = chart.legend.options;
    let hoverPoints = chart.hoverPoints;

    if ( !hoverPoints && chart.hoverPoint ) {
        hoverPoints = [ chart.hoverPoint ];
    }
    if ( hoverPoints ) {
        hoverPoints.forEach( point => {
            point.series.point = point;
        });
        chart.legend.allItems.forEach( item => {
            item.legendItem.attr({
                text: legendOptions.labelFormat ? Highstock.format( legendOptions.labelFormat, item )
                                                : legendOptions.labelFormatter.call( item )
            });
        });
    }

    if ( chart.legend.title ) {
        if ( !showSingleLegendXValue ) {
            // the x-values are rendered in the formatter for each series, not here.
            chart.legend.title.textSetter('');
        } else if ( !hoverPoints ) {
            // Passing an empty object to generateXLegendString ultimately sets the title to a hyphen.
            // Set the title to a hyphen rather than an empty string.
            // Using an empty string would make the plot expand vertically a bit.
            // Kind of irritating when the plots are wiggling around whenever you mouseover/out a plot.
            chart.legend.title.textSetter( generateXLegendString( legendOptions, ({})) );
        } else if ( hoverPoints ) {
            chart.legend.title.textSetter( generateXLegendString( legendOptions, ( hoverPoints[0] )));
        }
        chart.legend.render();
    }
}

/**
 * Sets the formatting of the labels along the chart's x-axis.
 * @param labelFormat The format of the x-axis labels. Valid values are:
 *   `null`: Uses the default highstock formatter for x-axis values.
 *   `'auto'`: Formats the x-axis values as human-readable timestamps, and honors the date format setting (YYYY-MM-DD vs. YYYY-DDD).
 *   `'raw'`: Does no formatting of the x-axis values -- outputs the raw values.
 *   `'secondsSinceT0'`: Formats the values as number of seconds after the minimum x-axis value of the chart.
 */
export function setXAxisLabels( labelFormat: AxisFormat, offset = 0, timezone = 'utc', timeFormat = 'YYYY-MM-DD' ) {
    let formatter;
    let navigatorFormatter;

    if ( labelFormat === 'secondsSinceT0') {
        formatter = function() {
            return xAxisLabelFormatterT0( this );
        };
        navigatorFormatter = formatter;
    } else if ( labelFormat === 'raw' ) {
        formatter = function() {
            return this.value + offset;
        };
        navigatorFormatter = formatter;
    } else if ( labelFormat === 'auto' ) {
        formatter = function() {
            return xAxisLabelFormatterDefault( this );
        };
        navigatorFormatter = function() {
            const tempDate = moment.utc( new Date( this.value + offset ) );
            tempDate.tz( timezone );
            return tempDate.format( 'HH:mm' );
        };
    } else if ( labelFormat === null || labelFormat === undefined ) {
        // use the default highcharts formatter
        formatter = null;
        navigatorFormatter = null;
    } else {
        console.error( 'Programmer error. Unrecognized labelFormat:', labelFormat );
    }

    // apply the settings to the highchart object
    return {
        formatter: formatter,
        navigatorFormatter: navigatorFormatter
    };

    /* highstockAxisLabelObj is the object assigned to 'this' in the axis label formatter function defined in the highstock config object.
    * These xAxisLabelFormatter functions are intended to be used only in this context.
    */
    function xAxisLabelFormatterDefault( highstockAxisLabelObj ) {
        const tempDate = moment.utc( new Date( highstockAxisLabelObj.value + offset ) );
        tempDate.tz( timezone );
        return tempDate.format( timeFormat + '[<br>]HH:mm:ss');
    }
    function xAxisLabelFormatterT0( highstockAxisLabelObj ) {
        return '+'
                + ( Math.round( (highstockAxisLabelObj.value - highstockAxisLabelObj.chart.xAxis[0].getExtremes().dataMin) / 1000) )
                + 's';
    }
}
